/**
 * Coast Guard forms that print the whole incident and need no picker: the
 * organization chart, the two check-in sheets, the demob tracking table and
 * the meeting schedule.
 *
 * Each is authored somewhere else in the app; the card is the printed view.
 */
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useManualCheckIn } from 'components/IAP/hooks/resourceInIncidentHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import CheckInDrawer from 'components/IAP/Resources/Drawers/CheckInDrawer';
import { RESOURCE_STATUSES } from 'components/IAP/Resources/constants/resourceConstants';
import { useCurrentUserApprovalAuthority } from 'hooks/incident/useCurrentUserApprovalAuthority';

import RosterOrgChartBox from '../RosterOrgChartBox';
import {
  ICS207_BOXES,
  ICS211A_BOXES,
  ICS211_BOXES,
  ICS230_BOXES,
  ICS238_BOXES,
} from './documentBoxLabels';
import { flattenPreparedBy } from './documentDerivations';
import { IcsDocumentCard } from './IcsDocumentCard';

function AddDocumentationRecordToolbar({ testId }: { testId: string }) {
  const [open, setOpen] = useState(false);
  const hasApprovalAuthority = useCurrentUserApprovalAuthority();
  const manualCheckIn = useManualCheckIn();

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid={testId}
        disabled={!hasApprovalAuthority || manualCheckIn.isPending}
        onClick={() => setOpen(true)}
      >
        Add documentation record
      </Button>
      {open ? (
        <CheckInDrawer
          resource={{}}
          isWalkUp
          onClose={() => setOpen(false)}
          onSave={(data) => {
            void manualCheckIn.mutateAsync({
              ...data,
              current_status: RESOURCE_STATUSES.AVAILABLE,
            });
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

export function Ics207DocumentCard() {
  const { effectivePeriodNumber } = useICSContext();
  const incidentId = useActualIncidentId();

  return (
    <IcsDocumentCard
      title="Incident Organization Chart ICS-207"
      formname="ICS_207_CG"
      exportLabel="Preview ICS-207"
      boxes={ICS207_BOXES}
      derive={flattenPreparedBy}
      periodNumber={effectivePeriodNumber}
      editedIn={{
        label: 'the incident roster',
        to: `/iap/${incidentId}/roster`,
      }}
      slots={{
        orgChart: (
          <RosterOrgChartBox emptyMessage="No one is on the roster yet. Seats filled on the Incident Roster appear here and print in Box 4." />
        ),
      }}
      testIdPrefix="ics207-document"
    />
  );
}

export function Ics211DocumentCard() {
  const { effectivePeriodNumber } = useICSContext();
  const incidentId = useActualIncidentId();
  return (
    <IcsDocumentCard
      title="Incident Check-in List ICS-211"
      formname="ICS_211_CG"
      exportLabel="Preview ICS-211"
      boxes={ICS211_BOXES}
      periodNumber={effectivePeriodNumber}
      editedIn={{
        label: 'resource check-in',
        to: `/iap/${incidentId}/resources`,
      }}
      versionBaseId="ICS_211"
      toolbar={
        <AddDocumentationRecordToolbar testId="ics211-document-add-record" />
      }
      testIdPrefix="ics211-document"
    />
  );
}

export function Ics238DocumentCard() {
  const { effectivePeriodNumber } = useICSContext();
  const incidentId = useActualIncidentId();
  return (
    <IcsDocumentCard
      title="Demob Tracking Table ICS-238"
      formname="ICS_238_CG"
      exportLabel="Preview ICS-238"
      boxes={ICS238_BOXES}
      periodNumber={effectivePeriodNumber}
      editedIn={{
        label: 'resource check-in and check-out',
        to: `/iap/${incidentId}/resources`,
      }}
      versionBaseId="ICS_238"
      toolbar={
        <AddDocumentationRecordToolbar testId="ics238-document-add-record" />
      }
      testIdPrefix="ics238-document"
    />
  );
}

export function Ics211ADocumentCard() {
  const { effectivePeriodNumber } = useICSContext();
  return (
    <IcsDocumentCard
      title="Daily Personnel Sign-In Sheet ICS-211A"
      formname="ICS_211A_CG"
      exportLabel="Preview ICS-211A"
      boxes={ICS211A_BOXES}
      periodNumber={effectivePeriodNumber}
      editedIn={{ label: 'the daily personnel sign-in sheet' }}
      testIdPrefix="ics211a-document"
    />
  );
}

export function Ics230DocumentCard() {
  const { effectivePeriodNumber } = useICSContext();
  return (
    <IcsDocumentCard
      title="Daily Meeting Schedule ICS-230"
      formname="ICS_230_CG"
      exportLabel="Preview ICS-230"
      boxes={ICS230_BOXES}
      periodNumber={effectivePeriodNumber}
      editedIn={{ label: 'the incident calendar' }}
      testIdPrefix="ics230-document"
    />
  );
}
